import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * #180 磁性文字动画 (Magnetic Text Animation)
 *
 * 技术要点：
 * - 文字拆分为单个字符
 * - 弹簧物理（胡克定律 + 阻尼）
 * - 磁场半径检测
 * - 基于吸引角度的旋转
 * - 模拟鼠标跟随效果
 */
export const MagneticText: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  const text = 'MAGNETIC';

  // 伪随机函数（使用固定种子）
  const seededRandom = (seed: number) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };

  // 模拟鼠标位置（移动轨迹）
  const mouseCenterX = interpolate(
    Math.sin(frame * 0.08),
    [-1, 1],
    [width * 0.3, width * 0.7]
  );
  const mouseCenterY = interpolate(
    Math.cos(frame * 0.06),
    [-1, 1],
    [height * 0.3, height * 0.7]
  );

  // 磁场半径
  const magneticRadius = 200;

  // 文字基准位置
  const baseX = width / 2;
  const baseY = height / 2;
  const fontSize = 70;
  const letterSpacing = 10;
  const totalWidth = text.length * (fontSize + letterSpacing);
  const startX = baseX - totalWidth / 2;

  // 计算每个字符的位置和旋转
  const characters = text.split('').map((char, i) => {
    const baseCharX = startX + i * (fontSize + letterSpacing);
    const baseCharY = baseY;

    // 计算与鼠标的距离
    const dx = mouseCenterX - baseCharX;
    const dy = mouseCenterY - baseCharY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // 磁场强度（距离越近，强度越大）
    const strength = interpolate(distance, [0, magneticRadius], [1, 0], {
      extrapolateRight: 'clamp',
    });

    // 弹簧物理动画
    const springProgress = spring({
      frame: frame - i * 3,
      fps,
      config: {
        damping: 12,
        stiffness: 100,
        mass: 0.5,
      },
    });

    // 计算位移（模拟吸引力）
    const maxOffset = 50;
    const offsetX = dx * strength * 0.3 * springProgress;
    const offsetY = dy * strength * 0.3 * springProgress;

    // 计算旋转角度（基于吸引方向）
    const angle = Math.atan2(dy, dx);
    const rotation = interpolate(strength, [0, 1], [0, 30]);

    // 计算缩放（靠近时放大）
    const scale = 1 + strength * 0.3;

    // 颜色渐变（基于位置）
    const hue = (i * 45 + frame) % 360;

    return {
      char,
      x: baseCharX + offsetX,
      y: baseCharY + offsetY,
      rotation: angle * (180 / Math.PI) * strength * 0.3,
      scale,
      opacity: springProgress,
      hue,
      strength,
    };
  });

  // 生成磁场可视化
  const fieldLines = Array.from({ length: 12 }, (_, i) => {
    const angle = (i / 12) * Math.PI * 2;
    const radius = magneticRadius * (0.8 + Math.sin(frame * 0.1 + i) * 0.2);
    const opacity = interpolate(
      frame,
      [0, 30],
      [0, 0.3],
      { extrapolateRight: 'clamp' }
    );

    return {
      x: mouseCenterX + Math.cos(angle) * radius,
      y: mouseCenterY + Math.sin(angle) * radius,
      opacity,
    };
  });

  // 生成装饰粒子
  const particles = Array.from({ length: 20 }, (_, i) => {
    const seed = seededRandom(i);
    const angle = seed * Math.PI * 2;
    const radius = magneticRadius * (0.5 + seededRandom(i + 100));
    const speed = seededRandom(i + 200) * 0.05 + 0.02;

    return {
      x: mouseCenterX + Math.cos(angle + frame * speed) * radius,
      y: mouseCenterY + Math.sin(angle + frame * speed) * radius,
      size: seededRandom(i + 300) * 4 + 2,
      opacity: interpolate(
        frame,
        [i * 2, i * 2 + 10, i * 2 + 30],
        [0, 0.8, 0],
        { extrapolateRight: 'clamp' }
      ),
      hue: (seed * 360 + frame) % 360,
    };
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        overflow: 'hidden',
      }}
    >
      {/* 磁场可视化 */}
      <svg width={width} height={height} style={{ position: 'absolute' }}>
        {/* 磁场范围 */}
        <circle
          cx={mouseCenterX}
          cy={mouseCenterY}
          r={magneticRadius}
          fill="none"
          stroke="rgba(255, 255, 255, 0.2)"
          strokeWidth={2}
          strokeDasharray="10 5"
        />

        {/* 磁场线 */}
        {fieldLines.map((line, i) => (
          <circle
            key={i}
            cx={mouseCenterX}
            cy={mouseCenterY}
            r={(i + 1) * (magneticRadius / 4)}
            fill="none"
            stroke={`hsla(${(i * 30 + frame) % 360}, 70%, 60%, ${line.opacity})`}
            strokeWidth={1}
          />
        ))}

        {/* 连接线（鼠标到字符） */}
        {characters.map((char, i) => (
          <line
            key={i}
            x1={char.x + fontSize / 2}
            y1={char.y}
            x2={mouseCenterX}
            y2={mouseCenterY}
            stroke={`hsla(${char.hue}, 70%, 60%, ${char.strength * 0.5})`}
            strokeWidth={2}
          />
        ))}
      </svg>

      {/* 装饰粒子 */}
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: p.x,
            top: p.y,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: `hsla(${p.hue}, 80%, 70%, ${p.opacity})`,
            boxShadow: `0 0 ${p.size * 2}px hsla(${p.hue}, 80%, 70%, 0.6)`,
          }}
        />
      ))}

      {/* 磁性文字 */}
      {characters.map((char, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: char.x,
            top: char.y,
            fontSize: fontSize * char.scale,
            fontWeight: 'bold',
            color: `hsl(${char.hue}, 70%, 70%)`,
            fontFamily: 'Arial, sans-serif',
            opacity: char.opacity,
            transform: `
              translateY(-50%)
              rotate(${char.rotation}deg)
            `,
            textShadow: `0 0 20px hsla(${char.hue}, 70%, 70%, 0.8)`,
          }}
        >
          {char.char}
        </div>
      ))}

      {/* 鼠标位置指示器 */}
      <div
        style={{
          position: 'absolute',
          left: mouseCenterX,
          top: mouseCenterY,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.8)',
          transform: 'translate(-50%, -50%)',
          boxShadow: '0 0 30px rgba(255, 255, 255, 0.6)',
        }}
      />

      {/* 说明文字 */}
      <div
        style={{
          position: 'absolute',
          bottom: 50,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontSize: 24,
          color: 'rgba(255, 255, 255, 0.7)',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        磁性文字动画
      </div>
    </AbsoluteFill>
  );
};
